import type { RecordObject } from "@/profitability/records/objects";
import type { ReportDataset, ReportOutput } from "@/profitability/types";

export interface DatasetUse {
  id: string;
  /** The object's plural, else the dataset's title. */
  name: string;
  record: boolean;
  /** The columns' labels, in the dataset's order. */
  columns: string[];
}

const datasetName = (dataset: ReportDataset, objects: RecordObject[]) =>
  objects.find((object) => object.dataset.id === dataset.id)?.plural ??
  dataset.title ??
  dataset.id;

const describe = (
  dataset: ReportDataset,
  objects: RecordObject[],
  columns: string[],
): DatasetUse => ({
  id: dataset.id,
  name: datasetName(dataset, objects),
  record: objects.some((object) => object.dataset.id === dataset.id),
  columns,
});

/**
 * What a file fed in the period's report: the columns whose `source` is the
 * slot it filled, grouped by dataset. `declared` is false when no column of
 * the report says where it comes from, so the page can say it doesn't know
 * rather than say the file fed nothing; `datasets` is then every dataset the
 * run produced.
 */
export const usedFor = (
  output: ReportOutput | undefined,
  objects: RecordObject[],
  slotKey: string | null,
): { declared: boolean; datasets: DatasetUse[] } => {
  const datasets = output?.datasets ?? [];
  const declared = datasets.some((dataset) =>
    dataset.columns.some((column) => column.source),
  );
  if (!declared)
    return {
      declared,
      datasets: datasets.map((dataset) => describe(dataset, objects, [])),
    };
  return {
    declared,
    datasets: slotKey
      ? datasets.flatMap((dataset) => {
          const columns = dataset.columns
            .filter((column) => column.source === slotKey)
            .map((column) => column.label ?? column.key);
          return columns.length ? [describe(dataset, objects, columns)] : [];
        })
      : [],
  };
};
